import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Collection, Element, Folder, Habit, Task } from '../../types';
import { advanceHabitProgressDate } from '../../utils/habitUtils';
import { removeNodeById, updateNodeById } from '../../utils/treeUtils';
import { getLocalDateKey } from './dateUtils';
import { getAllDayTaskDateUpdates, getRescheduledDate, getTaskDateUpdates } from './taskReschedule';
import { createNextRecurringTask, isRecurringTaskDue } from '../../utils/taskRecurrence';
import { getHabitsForDate, getTasksWithDueDates, getUnscheduledTasks } from './taskUtils';
import { PlannerTaskInfo } from './types';

export const usePlannerData = (
    folders: Folder[],
    setFolders: (folders: Folder[]) => void,
    includeCompletedTasks: boolean,
) => {
    const { t } = useTranslation();
    const updateCollection = useCallback((id: string, update: (collection: Collection) => Collection) => {
        setFolders(folders.map((folder) => ({
            ...folder,
            collections: (folder.collections || []).map((collection) => collection.id === id ? update(collection) : collection),
        })));
    }, [folders, setFolders]);
    const updateTask = useCallback((
        collectionId: string, taskId: string, updates: Partial<Task> | ((task: Task) => Partial<Task>)
    ) => updateCollection(collectionId, (collection) => ({
        ...collection,
        elements: updateNodeById(collection.elements || [], taskId, (element) => {
            const next = typeof updates === 'function' ? updates(element as Task) : updates;
            return { ...element, ...next } as Element;
        }),
    })), [updateCollection]);
    const getFolderName = useCallback((id: string) => (
        folders.find((folder) => folder.id === id)?.name || t('dailyPlanner.unknownFolder')
    ), [folders, t]);
    const getCollectionName = useCallback((id: string) => {
        for (const folder of folders) {
            const collection = folder.collections?.find((entry) => entry.id === id);
            if (collection) return collection.name;
        }
        return t('dailyPlanner.unknownCollection');
    }, [folders, t]);
    const tasksWithDueDates = useMemo(
        () => getTasksWithDueDates(folders, includeCompletedTasks),
        [folders, includeCompletedTasks],
    );
    const unscheduledTasks = useMemo(() => getUnscheduledTasks(folders), [folders]);
    const habitsForToday = useMemo(() => getHabitsForDate(folders, new Date()), [folders]);
    const handleDeleteTask = useCallback((taskId: string, collectionId: string) => {
        updateCollection(collectionId, (collection) => ({
            ...collection, elements: removeNodeById(collection.elements || [], taskId),
        }));
    }, [updateCollection]);
    const handleToggleComplete = useCallback((taskId: string, collectionId: string) => {
        updateCollection(collectionId, (collection) => {
            let nextRecurringTask: Task | undefined;
            const elements = updateNodeById(collection.elements || [], taskId, (element) => {
                const task = element as Task;
                const completed = !task.completed;
                const isFutureCompletionLocked = !!task.recurrence
                    && task.recurrence.mode === 'completion'
                    && !isRecurringTaskDue(task);
                if (isFutureCompletionLocked) return task;
                if (completed && task.recurrence) nextRecurringTask = createNextRecurringTask(task);
                return {
                    ...task,
                    completed,
                    completedAt: completed ? new Date().toISOString() : undefined,
                    recurrence: completed ? undefined : task.recurrence,
                    recurrencePending: undefined,
                } as Element;
            });
            return {
                ...collection,
                elements: nextRecurringTask ? [...elements, nextRecurringTask] : elements,
            };
        });
    }, [updateCollection]);
    const handleAdvanceHabit = useCallback((habitId: string, collectionId: string) => {
        const date = getLocalDateKey(new Date());
        updateCollection(collectionId, (collection) => ({
            ...collection,
            elements: (collection.elements || []).map((element) => (
                element.type === 'habit' && element.id === habitId
                    ? { ...element, ...advanceHabitProgressDate(element as Habit, date) } : element
            )),
        }));
    }, [updateCollection]);
    const handleRescheduleTask = useCallback((taskInfo: PlannerTaskInfo, option: string, customTime?: string) => {
        if (option === 'later-someday') {
            updateTask(taskInfo.collectionId, taskInfo.task.id, { dueDate: undefined, schedule: undefined });
            return;
        }
        if (option === 'today-all-day') {
            updateTask(taskInfo.collectionId, taskInfo.task.id, getAllDayTaskDateUpdates(new Date()));
            return;
        }
        const date = getRescheduledDate(taskInfo, option, tasksWithDueDates, customTime);
        if (date) updateTask(taskInfo.collectionId, taskInfo.task.id, getTaskDateUpdates(taskInfo.task, date));
    }, [tasksWithDueDates, updateTask]);
    return {
        getCollectionName, getFolderName, handleAdvanceHabit, handleDeleteTask, handleRescheduleTask,
        handleToggleComplete, habitsForToday, tasksWithDueDates,
        unscheduledTasks, updateCollection, updateTask,
    };
};

export type PlannerData = ReturnType<typeof usePlannerData>;
